import { Result, setResultAsStale } from "../Utility/Result"

/**
 * `FetchState` holds fetch state of a remote resource.
 *
 * It can be one of these:
 * - `undefined` means the resource is not requested yet
 * - `"loading"` means the resource is requested but not yet received
 * - `Ok` means the resource is received successfully
 * - `Error` means failed to fetch resource
 */
export type FetchState<M> = Result<M> | "loading" | undefined

/**
 * Checks if the argument is `Ok`.
 * This is useful in writing a guard before accessing underlying `value` of `Ok`.
 */
export function hasValue<M>(
    data: FetchState<M>
): data is { isError: false; value: M } {
    return hasResult(data) && !data.isError
}

/**
 * Returns value if the argument is `Ok`.
 */
export function getValue<M>(data: FetchState<M>): M | undefined {
    return hasValue(data) ? data.value : undefined
}

/**
 * Checks if the argument is `Ok` or `Error`.
 */
export function hasResult<M>(data: FetchState<M>): data is Result<M> {
    return data !== undefined && data !== "loading"
}

/**
 * Checks if the data is loading.
 *
 */
export function isLoading<M>(data: FetchState<M>): data is "loading" {
    return data === "loading"
}

export function isStale<M>(data: FetchState<M>): data is Result<M> {
    return hasValue(data) ? !!data.isStale : false
}

export function setStale<M>(data: FetchState<M>): FetchState<M> {
    return hasValue(data) ? setResultAsStale(data) : data
}
